import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Bell,
  BellDot,
  BellRing,
  // Calendar,
  // Clock,
  Download,
  History,
  Mail,
  MailWarning,
  RefreshCw,
  Search,
  Settings,
} from 'lucide-react'
import {
  useFetchByReference,
  useFetchFromDate,
  useManualFetchEmails,
  useManualSendNotifications,
  useNotifyUnmatchedDonors,
  // useServiceConfig,
  useServiceStatus,
  useTriggerRecovery,
  // useUpdateConfig,
} from '../hooks/useServiceConfig'
import { ServiceActivityList } from '../components/ServiceActivityList'
// import type { ServiceConfig } from '../api/serviceConfigApi'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
// import { Label } from '@/components/ui/label'
import DatePicker from '@/components/custom/date-picker'
import Loader from '@/components/Loader'
import { useIsMobile } from '@/hooks'
import { dateInToronto } from '@/utils/timeZoneUtils'
import { usePermissions } from '@/utils/permissions'

export const ServiceConfiguration = () => {
  const isMobile = useIsMobile()
  // const [config, setConfig] = useState<ServiceConfig & { backupTime?: string }>(
  //   {
  //     emailFetchTime: '01:00',
  //     notificationSendTime: '08:00',
  //     backupTime: '02:00',
  //   },
  // )

  const permissions = usePermissions()
  const isSystemAdmin = permissions.isSystemAdmin()
  const isDeveloper = permissions.isDeveloper()

  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const oneYearAgo = new Date()
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

  const [fromDate, setFromDate] = useState<Date | undefined>(yesterday)
  const [refNumber, setRefNumber] = useState('')

  const fromDateField = {
    state: {
      value: fromDate,
      meta: { isTouched: false, isValid: true, errors: [], isValidating: false },
    },
    handleChange: (date: Date | undefined) => {
      setFromDate(date)
      fetchFromDateMutation.reset()
    },
  }

  // React Query hooks
  // const { data: configData, isLoading: isConfigLoading } = useServiceConfig()

  const {
    data: serviceStatusData,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
    refetch: refetchServiceStatus,
  } = useServiceStatus()

  // const updateConfigMutation = useUpdateConfig()
  const fetchFromDateMutation = useFetchFromDate()
  const fetchByReferenceMutation = useFetchByReference()
  const manualFetchMutation = useManualFetchEmails()
  const manualNotificationMutation = useManualSendNotifications()
  const triggerRecoveryMutation = useTriggerRecovery()
  const notifyUnmatchedDonationsMutation = useNotifyUnmatchedDonors()

  const recentServices = serviceStatusData?.recentServices || []
  // const isLoading = isConfigLoading || isStatusLoading
  const isLoading = isStatusLoading

  const lastFetchFailed = useMemo(
    () =>
      recentServices.filter((s) => s.serviceName === 'email_fetch')[0]
        ?.status !== 'completed',
    [recentServices],
  )
  const lastNotifyFailed = useMemo(
    () =>
      recentServices.filter((s) => s.serviceName === 'notification_send')[0]
        ?.status !== 'completed',
    [recentServices],
  )

  // Initialize config from server data if available
  // useEffect(() => {
  //   if (configData?.schedules) {
  //     setConfig((prev) => ({
  //       ...prev,
  //       emailFetchTime: configData.schedules.emailFetchTime,
  //       notificationSendTime: configData.schedules.notificationSendTime,
  //       backupTime:
  //         configData.schedules.backupTime || prev.backupTime || '03:00',
  //     }))
  //   }
  // }, [configData])

  // Redirect if not super admin
  useEffect(() => {
    if (!isSystemAdmin) {
      toast.error('You need System Admin privileges to access this page.')
      return
    }
  }, [isSystemAdmin])

  // const updateScheduleConfig = () => {
  //   updateConfigMutation.mutate(config)
  // }

  const manualFetchEmails = () => {
    manualFetchMutation.mutate()
  }

  const manualSendNotifications = () => {
    manualNotificationMutation.mutate()
  }

  const triggerRecovery = () => {
    triggerRecoveryMutation.mutate()
  }

  const notifyUnmatchedDonations = () => {
    notifyUnmatchedDonationsMutation.mutate()
  }

  if (!isSystemAdmin) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Restricted
          </h3>
          <p className="text-gray-500">
            System Admin privileges required to access service configuration.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h1 className="text-3xl font-bold">Donation Automation Dashboard</h1>
      </div>
      <p className="text-gray-600">
        Configure automated donation processes and perform manual operations.
      </p>

      {/* Schedule Configuration */}
      {/* <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-6`}> */}
      {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Service Schedule
            </CardTitle>
            <CardDescription>
              Configure when automated services should run (EST/EDT timezone)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="emailFetchTime">Email Fetch Time</Label>
                <Input
                  id="emailFetchTime"
                  type="time"
                  value={config.emailFetchTime}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      emailFetchTime: e.target.value,
                    }))
                  }
                  disabled={updateConfigMutation.isPending}
                />
                <p className="text-sm text-gray-500">
                  Daily e-transfer email fetching
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notificationSendTime">Notification Time</Label>
                <Input
                  id="notificationSendTime"
                  type="time"
                  value={config.notificationSendTime}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      notificationSendTime: e.target.value,
                    }))
                  }
                  disabled={updateConfigMutation.isPending}
                />
                <p className="text-sm text-gray-500">
                  Daily pending donation notifications
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="backupTime">Backup Time</Label>
                <Input
                  id="backupTime"
                  type="time"
                  value={config.backupTime}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      backupTime: e.target.value,
                    }))
                  }
                  disabled={updateConfigMutation.isPending}
                />
                <p className="text-sm text-gray-500">Daily database backup</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={updateScheduleConfig}
                disabled={updateConfigMutation.isPending}
                className="w-full md:w-auto"
              >
                {updateConfigMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Calendar className="mr-2 h-4 w-4" />
                    Update Schedule
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card> */}

      {/* Manual Operations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Manual Operations
          </CardTitle>
          <CardDescription>
            Manually trigger service operations outside of scheduled times
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-center' : 'mb-4'}`}
            >
              <Button
                onClick={manualFetchEmails}
                disabled={manualFetchMutation.isPending}
                variant="outline"
                className={`w-full ${lastFetchFailed ? 'bg-amber-50 text-amber-700 border-amber-200' : ''}`}
              >
                {manualFetchMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    {lastFetchFailed ? (
                      <MailWarning className="h-4 w-4" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    Fetch E-Transfers
                  </>
                )}
              </Button>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Search for and process new e-transfer emails
              </p>
            </div>
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-center' : 'mb-4'}`}
            >
              <Button
                onClick={manualSendNotifications}
                disabled={manualNotificationMutation.isPending}
                variant="outline"
                className={`w-full ${lastNotifyFailed ? 'bg-amber-50 text-amber-700 border-amber-200' : ''}`}
              >
                {manualNotificationMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    {lastNotifyFailed ? (
                      <BellDot className="h-4 w-4" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    Send Notifications
                  </>
                )}
              </Button>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Send pending and undeposited donations notifications to
                treasurers
              </p>
            </div>
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-center' : 'mb-4'}`}
            >
              <Button
                onClick={triggerRecovery}
                disabled={triggerRecoveryMutation.isPending}
                variant="outline"
                className="w-full"
              >
                {triggerRecoveryMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Triggering...
                  </>
                ) : (
                  <>
                    <RefreshCw className=" h-4 w-4" />
                    Trigger Recovery
                  </>
                )}
              </Button>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Check for and recover any missed scheduled services
              </p>
            </div>
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-center' : ''}`}
            >
              <Button
                onClick={notifyUnmatchedDonations}
                disabled={notifyUnmatchedDonationsMutation.isPending}
                variant="outline"
                className="w-full"
              >
                {notifyUnmatchedDonationsMutation.isPending ? (
                  <>
                    <BellRing className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <BellRing className=" h-4 w-4" />
                    Notify Unmatched Donations
                  </>
                )}
              </Button>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Send notification emails to treasurers about unmatched donations
              </p>
            </div>
          </div>
          {/* <div className="text-sm text-gray-600 space-y-1">
            <p>
              <strong>Fetch E-Transfers:</strong> Manually search for and
              process new e-transfer emails
            </p>
            <p>
              <strong>Send Notifications:</strong> Manually send pending and
              undeposited donations notifications to treasurers and super admins
            </p>
            <p>
              <strong>Trigger Recovery:</strong> Check for and recover any
              missed scheduled services
            </p>
            <p>
              <strong>Notify Unmatched Donations:</strong> Send notification
              emails to treasurers about unmatched donations
            </p>
          </div> */}
        </CardContent>
      </Card>
      {/* </div> */}

      {/* Historical E-Transfer Recovery — Developer only */}
      {isDeveloper && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historical E-Transfer Recovery
            </CardTitle>
            <CardDescription>
              Fetch all e-transfer emails from a specific date forward. Emails
              already in the database are skipped automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-start' : ''}`}
            >
              <div className="space-y-3">
                <DatePicker
                  field={fromDateField}
                  label=""
                  placeholder="Pick a start date"
                  disableFutureDates
                  minDate={oneYearAgo}
                  disabled={fetchFromDateMutation.isPending}
                  className="max-w-full"
                />
                <Button
                  onClick={() =>
                    fromDate &&
                    fetchFromDateMutation.mutate(
                      dateInToronto(fromDate).format('YYYY-MM-DD'),
                    )
                  }
                  disabled={fetchFromDateMutation.isPending || !fromDate}
                  variant="outline"
                  className="w-full"
                >
                  {fetchFromDateMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <History className="h-4 w-4" />
                      Fetch E-Transfers from Date
                    </>
                  )}
                </Button>
                {fetchFromDateMutation.isSuccess && (
                  <p className="text-sm text-green-600">
                    Processed{' '}
                    <strong>{fetchFromDateMutation.data.processed}</strong> new
                    donation(s), skipped{' '}
                    <strong>{fetchFromDateMutation.data.skipped}</strong> already
                    in DB.
                  </p>
                )}
              </div>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Use this to recover missed scheduled fetches. Select the date of
                the first missed run and all e-transfers from that day onward
                will be imported.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Reference Recovery — Developer only */}
      {isDeveloper && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Manual Reference Recovery
            </CardTitle>
            <CardDescription>
              Look up one or more e-transfers by reference number. Comma-separate
              multiple values. DB is checked first; missing ones are fetched from
              Gmail and created automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`${!isMobile ? 'grid grid-cols-2 gap-4 items-start' : ''}`}
            >
              <div className="space-y-3">
                <Input
                  placeholder="e.g. C1AbCdEfGhIj, CAbc1234, C1XyZwVuTs"
                  value={refNumber}
                  onChange={(e) => {
                    setRefNumber(e.target.value)
                    fetchByReferenceMutation.reset()
                  }}
                  disabled={fetchByReferenceMutation.isPending}
                />
                <Button
                  onClick={() => {
                    const refs = refNumber
                      .split(',')
                      .map((r) => r.trim())
                      .filter(Boolean)
                    fetchByReferenceMutation.mutate(refs)
                  }}
                  disabled={
                    fetchByReferenceMutation.isPending || !refNumber.trim()
                  }
                  variant="outline"
                  className="w-full"
                >
                  {fetchByReferenceMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Recover Donation(s)
                    </>
                  )}
                </Button>
                {fetchByReferenceMutation.isSuccess && (
                  <div className="space-y-1 text-sm">
                    {fetchByReferenceMutation.data.results.map((r) => (
                      <div key={r.referenceNumber}>
                        {r.status === 'found_in_db' && (
                          <p className="text-green-600">
                            <strong>{r.referenceNumber}</strong> — Already in DB · Receipt:{' '}
                            <strong>{r.receiptNumber}</strong>
                          </p>
                        )}
                        {r.status === 'created' && (
                          <p className="text-green-600">
                            <strong>{r.referenceNumber}</strong> — Created · Receipt:{' '}
                            <strong>{r.receiptNumber}</strong>
                          </p>
                        )}
                        {r.status === 'not_found_in_gmail' && (
                          <p className="text-amber-600">
                            <strong>{r.referenceNumber}</strong> — Not found in Gmail
                          </p>
                        )}
                        {r.status === 'parse_failed' && (
                          <p className="text-red-600">
                            <strong>{r.referenceNumber}</strong> — Found but could not be parsed
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className={`text-sm text-gray-500 ${isMobile ? 'mt-1' : ''}`}>
                Enter one or more Interac e-transfer reference numbers separated
                by commas. Each is checked in DB first; if missing, Gmail is
                searched and the donation is created automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Service Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              <div>
                <CardTitle>Recent Service Activity</CardTitle>
                <CardDescription>
                  Latest execution status of automated services
                </CardDescription>
              </div>
            </div>
            <Button
              onClick={() => refetchServiceStatus()}
              variant="outline"
              size="sm"
              disabled={isStatusFetching}
              title="Refresh Service Status"
            >
              {isStatusFetching ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ServiceActivityList recentServices={recentServices} />
        </CardContent>
      </Card>
    </div>
  )
}
